import { useEffect, useRef, useState } from "react";
import { itemById, unitById, zombieById, type Resource, type RewardBundle } from "@zend/content";
import { AssetIcon } from "../assets/Icon.js";
import { ResourceIcon } from "../assets/gameIcons.js";
import { api, ApiError } from "../api/http.js";
import { pick } from "../i18n/index.js";
import { navigate } from "../router.js";
import { useUnits } from "../units/state.js";
import { Button } from "../ui/Button.js";
import { Icon } from "../ui/Icon.js";
import { Dialog } from "../ui/Dialog.js";
import { Timer } from "../ui/Timer.js";
import { RepairPanel } from "../battle/Aftermath.js";
import type { NexusRun, NexusState } from "./types.js";
import "./nexus.css";

const words = (de: string, en: string) => pick({ de, en });
function failure(error: unknown) {
  const code = error instanceof ApiError ? error.code : "";
  const messages: Record<string, [string, string]> = {
    NEXUS_ACTIVE: [
      "Dein Einsatz läuft bereits. Ansicht aktualisiert.",
      "Your run is already active. View refreshed.",
    ],
    NEXUS_LOCKED: [
      "HQ ausbauen und den vorherigen Sektor abschließen.",
      "Upgrade HQ and clear the previous sector.",
    ],
    NEXUS_DAILY: ["Heute sind alle Eintritte verbraucht.", "All entries used today."],
    NEXUS_WAVE: [
      "Diese Welle ist bereits entschieden. Ansicht aktualisiert.",
      "This wave is already decided. View refreshed.",
    ],
    NEXUS_RETRY: [
      "Die Welle wurde mit einer anderen Auswahl gestartet. Öffne ihren Bericht.",
      "The wave started with a different selection. Open its report.",
    ],
    NO_SQUAD: ["Wähle einen einsatzfähigen Trupp.", "Select a battle-ready squad."],
    SQUAD_BUSY: [
      "Dieser Trupp ist unterwegs. Wähle einen freien Trupp.",
      "This squad is deployed. Choose an idle squad.",
    ],
    REPAIRING: [
      "Eine Einheit dieses Trupps wird repariert. Wähle einen anderen Trupp oder warte auf die Reparatur.",
      "A unit in this squad is repairing. Choose another squad or wait for repairs.",
    ],
    INSUFFICIENT_STAMINA: ["Nicht genug Treibstoff für den Eintritt.", "Not enough fuel for entry."],
  };
  const text = messages[code];
  return text
    ? words(...text)
    : words(
        "Aktion fehlgeschlagen. Prüfe den Einsatz und versuche es erneut.",
        "Action failed. Check your run and try again.",
      );
}

function Rewards({ bundle }: { bundle: RewardBundle }) {
  return (
    <div className="nexus-rewards">
      {(bundle.items ?? []).map((item) => (
        <div key={item.itemId}>
          <AssetIcon id={item.itemId} size={70} />
          <strong>×{item.qty}</strong>
          <small>{pick(itemById(item.itemId).name)}</small>
        </div>
      ))}
      {Object.entries(bundle.resources ?? {}).map(([id, qty]) => (
        <div key={id}>
          <ResourceIcon resource={id as Resource} size={48} />
          <strong>
            {qty} {id}
          </strong>
        </div>
      ))}
    </div>
  );
}

/** Persistent portal: inspect, choose a squad, watch the real battle, then continue or extract. */
export function NexusView() {
  const [state, setState] = useState<NexusState | null>(null),
    [error, setError] = useState("");
  const [tier, setTier] = useState(1),
    [squadSlot, setSquadSlot] = useState(1),
    [tactic, setTactic] = useState("bastion");
  const [busy, setBusy] = useState(false),
    [confirm, setConfirm] = useState<"start" | "extract" | null>(null);
  const alive = useRef(true),
    lock = useRef(false),
    hydrated = useRef(false),
    sequence = useRef(0),
    nonce = useRef<{ tier: number; value: string } | null>(null);
  const roster = useUnits((s) => s.roster);
  async function refresh() {
    const version = ++sequence.current;
    try {
      const next = await api<NexusState>("GET", "/api/nexus");
      if (alive.current && version === sequence.current) {
        setState(next);
        if (!hydrated.current) {
          const last = next.run?.history.at(-1);
          if (last) {
            setSquadSlot(last.squadSlot);
            setTactic(last.tactic.id);
          }
          hydrated.current = true;
        }
        if (next.run?.status === "active") {
          nonce.current = null;
          setConfirm((value) => (value === "start" ? null : value));
        }
      }
    } catch (e) {
      if (alive.current && version === sequence.current) setError(failure(e));
    }
  }
  useEffect(() => {
    alive.current = true;
    void refresh();
    void useUnits.getState().refreshRoster();
    const update = () => {
      void refresh();
      void useUnits.getState().refreshRoster();
    };
    window.addEventListener("focus", update);
    const timer = setInterval(update, 15000);
    return () => {
      alive.current = false;
      sequence.current++;
      clearInterval(timer);
      window.removeEventListener("focus", update);
    };
  }, []);
  async function act(kind: "start" | "wave" | "extract") {
    if (lock.current || !state) return;
    lock.current = true;
    setBusy(true);
    setError("");
    sequence.current++;
    try {
      if (kind === "start") {
        if (!nonce.current || nonce.current.tier !== tier)
          nonce.current = { tier, value: crypto.randomUUID() };
        await api<{ run: NexusRun }>("POST", "/api/nexus/start", { tier, nonce: nonce.current.value });
        nonce.current = null;
      } else if (kind === "extract") {
        await api("POST", `/api/nexus/${state.run!.id}/extract`);
      } else {
        const out = await api<{ battleId: string }>("POST", `/api/nexus/${state.run!.id}/wave`, {
          wave: state.run!.cleared + 1,
          squadSlot,
          tactic,
        });
        if (alive.current) navigate(`/app/battle/${out.battleId}`);
      }
      if (alive.current) {
        setConfirm(null);
        await refresh();
        void useUnits.getState().refreshRoster();
        void useUnits.getState().refreshMe();
      }
    } catch (e) {
      if (alive.current) {
        setError(failure(e));
        await refresh();
      }
    } finally {
      lock.current = false;
      if (alive.current) setBusy(false);
    }
  }
  const run = state?.run,
    active = run?.status === "active",
    wave = run ? run.cleared + 1 : 1;
  const encounter = active ? run.encounters[wave - 1] : null,
    last = run?.history.at(-1);
  const squad = roster?.squads.find((s) => s.slot === squadSlot);
  const members = Object.values(squad?.members ?? {}).flatMap(
    (id) => roster?.units.find((u) => u.id === id) ?? [],
  );
  const anomaly = active ? run.anomaly : state?.anomaly;
  const earned: RewardBundle = { resources: {}, items: [] };
  for (const h of run?.history ?? []) {
    for (const [id, amount] of Object.entries(h.report.loot.resources))
      earned.resources![id as keyof NonNullable<RewardBundle["resources"]>] =
        (earned.resources![id as keyof NonNullable<RewardBundle["resources"]>] ?? 0) + amount;
    for (const item of h.report.loot.items) {
      const old = earned.items!.find((i) => i.itemId === item.itemId);
      if (old) old.qty += item.qty;
      else earned.items!.push({ ...item });
    }
  }
  return (
    <section className="nexus-panel" data-testid="nexus-panel" data-active={active}>
      <header className="nexus-heading">
        <div>
          <span>{words("ANOMALIE-EINSATZ", "ANOMALY OPERATION")}</span>
          <h1>NEXUS</h1>
        </div>
        <Button variant="ghost" onClick={() => navigate("/app/events")}>
          {words("Zurück", "Back")}
        </Button>
      </header>
      {error ? (
        <p role="alert" className="nexus-error">
          {error}
        </p>
      ) : null}
      {!state ? (
        <Button onClick={() => void refresh()}>{words("Einsatz laden", "Load operation")}</Button>
      ) : (
        <>
          <div className={`nexus-hero ${active ? "is-active" : ""}`}>
            <AssetIcon id="zmb.brute" size={240} />
            <div>
              <span>{words("5 WELLEN · SOLO", "5 WAVES · SOLO")}</span>
              <h2>{active ? pick(encounter!.title) : words("Sichere den Riss", "Secure the rift")}</h2>
              <p>
                {active
                  ? words(`Welle ${wave} / 5`, `Wave ${wave} / 5`)
                  : words(
                      "Trupp wählen. Risswächter besiegen. Ausrüstung bergen.",
                      "Choose your squad. Defeat the guardian. Recover equipment.",
                    )}
              </p>
              <div className="nexus-prize">
                <AssetIcon
                  id={(active ? run.tier : tier) > 1 ? "itm.chest_gold" : "itm.chest_silver"}
                  size={86}
                />
                <strong>{words("Abschlussbeute", "Final reward")}</strong>
              </div>
            </div>
          </div>
          <div className="nexus-anomaly">
            <strong>{pick(anomaly!.title)}</strong>
            <span>{pick(anomaly!.description)}</span>
            <small>
              {active
                ? words("Für diesen Einsatz festgelegt", "Fixed for this run")
                : words("Wöchentliche Anomalie", "Weekly anomaly")}
            </small>
          </div>
          {run ? (
            <ol className="nexus-waves" aria-label={words("Wellenfortschritt", "Wave progress")}>
              {run.encounters.map((e, i) => (
                <li
                  key={i}
                  data-cleared={i < run.cleared}
                  aria-current={active && i === run.cleared ? "step" : undefined}
                >
                  <b>{i < run.cleared ? <Icon name="check" /> : i + 1}</b>
                  <span>{pick(e.title)}</span>
                </li>
              ))}
            </ol>
          ) : null}
          {active && encounter ? (
            <>
              <div className="nexus-preparation">
                <section className="nexus-intel">
                  <h2>{words("Gegner", "Enemies")}</h2>
                  <div className="nexus-enemies">
                    {encounter.enemy.members.map((m) => (
                      <article key={m.id}>
                        <AssetIcon id={m.defId} size={120} />
                        <strong>{pick(zombieById(m.defId).name)}</strong>
                        <span>
                          Lv. {m.level} ·{" "}
                          {m.slot.startsWith("s") ? words("Support", "Support") : words("Front", "Front")}
                        </span>
                        <details>
                          <summary>{words("Werte", "Stats")}</summary>
                          <small>
                            HP {Math.round(m.stats.hp * (1 + (encounter.enemy.modifiers?.hpPct ?? 0) / 100))}{" "}
                            · ATK{" "}
                            {Math.round(m.stats.atk * (1 + (encounter.enemy.modifiers?.atkPct ?? 0) / 100))} ·
                            DEF{" "}
                            {Math.round(m.stats.def * (1 + (encounter.enemy.modifiers?.defPct ?? 0) / 100))}
                          </small>
                        </details>
                      </article>
                    ))}
                  </div>
                </section>
                <section>
                  <h2>{words("Dein Trupp", "Your squad")}</h2>
                  <div className="nexus-team-tabs">
                    {roster?.squads.map((s) => (
                      <button
                        key={s.slot}
                        aria-pressed={s.slot === squadSlot}
                        onClick={() => setSquadSlot(s.slot)}
                        disabled={busy}
                      >
                        {s.name} · {s.power}
                      </button>
                    ))}
                    <Button variant="ghost" onClick={() => navigate("/app/units/squads")}>
                      {words("Trupps aufbauen", "Build squads")}
                    </Button>
                  </div>
                  <div className="nexus-members">
                    {members.map((m) => (
                      <div key={m.id}>
                        <AssetIcon id={m.unitId} portrait size={78} />
                        <strong>{pick(unitById(m.unitId).name)}</strong>
                        <span>
                          Lv. {m.level} · {Math.round(m.hpPct)}% HP
                        </span>
                        {m.wrecked || m.repair ? (
                          <small className="nexus-error">{words("Nicht einsatzfähig", "Unavailable")}</small>
                        ) : null}
                      </div>
                    ))}
                  </div>
                  <div className="nexus-tactics">
                    {state.tactics.map((t) => (
                      <button
                        key={t.id}
                        aria-pressed={tactic === t.id}
                        onClick={() => setTactic(t.id)}
                        disabled={busy}
                      >
                        <AssetIcon id={t.icon} portrait size={64} />
                        <strong>{pick(t.title)}</strong>
                        <span>{pick(t.description)}</span>
                      </button>
                    ))}
                  </div>
                </section>
              </div>
              <details className="nexus-loot-preview">
                <summary>{words("Beute dieser Welle", "This wave's loot")}</summary>
                <Rewards bundle={encounter.reward} />
              </details>
              <div className="nexus-actions">
                <Button
                  variant="gold"
                  disabled={busy || !squad?.ready || !!squad.siegeRunId || members.some((m) => !!m.repair)}
                  onClick={() => void act("wave")}
                  data-testid="nexus-attack"
                >
                  {words(`Welle ${wave} angreifen`, `Attack wave ${wave}`)}
                </Button>
                <Button variant="ghost" disabled={busy} onClick={() => setConfirm("extract")}>
                  {words("Einsatz beenden", "Extract")}
                </Button>
              </div>
              <p className="nexus-footnote">
                {words(
                  "Kein weiterer Treibstoff. Schäden bleiben; Truppwechsel und Reparaturen sind zwischen Wellen möglich.",
                  "No extra fuel. Damage persists; switch squads and repair between waves.",
                )}
              </p>
            </>
          ) : (
            <>
              {run ? (
                <h2 role="status">
                  {run.status === "completed"
                    ? words("Riss gesichert — Beute geborgen", "Rift secured — loot recovered")
                    : run.status === "lost"
                      ? words("Trupp zurückgeschlagen", "Squad repelled")
                      : words("Einsatz beendet", "Extraction complete")}
                </h2>
              ) : null}
              <div className="nexus-tiers">
                {state.tiers.map((t) => (
                  <button
                    key={t.tier}
                    aria-pressed={tier === t.tier}
                    disabled={!t.unlocked || busy}
                    onClick={() => setTier(t.tier)}
                  >
                    <strong>{pick(t.title)}</strong>
                    <span>
                      HQ {t.hq}
                      {!t.unlocked ? words(" · vorherigen Sektor sichern", " · clear previous sector") : ""}
                    </span>
                  </button>
                ))}
              </div>
              <details className="nexus-entry-intel" data-testid="nexus-entry-intel">
                <summary>
                  {words("Gegner und Beute vor dem Eintritt prüfen", "Inspect enemies and loot before entry")}
                </summary>
                {state.tiers
                  .find((t) => t.tier === tier)
                  ?.encounters.map((e, i) => (
                    <article key={i}>
                      <h3>
                        {i + 1} · {pick(e.title)}
                      </h3>
                      <div className="nexus-entry-enemies">
                        {e.enemy.members.map((m) => (
                          <div key={m.id}>
                            <AssetIcon id={m.defId} size={68} />
                            <span>
                              {pick(zombieById(m.defId).name)} · Lv. {m.level}
                            </span>
                          </div>
                        ))}
                      </div>
                      <Rewards bundle={e.reward} />
                    </article>
                  ))}
              </details>
              <div className="nexus-entry">
                <span>
                  {words(`${state.runsLeft} / 3 Eintritte übrig`, `${state.runsLeft} / 3 entries left`)} ·{" "}
                  <Timer finishesAt={state.resetsAt} />
                </span>
                <span>
                  {words("Treibstoff", "Fuel")}: {state.stamina.current} / {state.stamina.cap}
                </span>
                <Button
                  variant="gold"
                  data-testid="nexus-start"
                  disabled={
                    busy ||
                    state.runsLeft === 0 ||
                    !state.tiers.find((t) => t.tier === tier)?.unlocked ||
                    state.stamina.current < state.entryStamina
                  }
                  onClick={() => setConfirm("start")}
                >
                  {words(
                    `Portal betreten · ${state.entryStamina} Treibstoff`,
                    `Enter portal · ${state.entryStamina} fuel`,
                  )}
                </Button>
                {state.hq < state.unlockHq ? (
                  <p>{words(`Freischaltung mit HQ ${state.unlockHq}`, `Unlocks at HQ ${state.unlockHq}`)}</p>
                ) : null}
              </div>
            </>
          )}
          {last ? (
            <section className="nexus-after">
              <h2>{words("Bereits im Lager", "Already in inventory")}</h2>
              <Rewards bundle={earned} />
              <Button variant="ghost" onClick={() => navigate("/app/inventory")}>
                {words("Lager öffnen", "Open inventory")}
              </Button>
              <details>
                <summary>{words("Berichte und Reparaturen", "Reports and repairs")}</summary>
                <div className="nexus-history">
                  {run!.history.map((h) => (
                    <Button key={h.wave} onClick={() => navigate(`/app/battle/${h.battleId}`)}>
                      {words(`Welle ${h.wave}`, `Wave ${h.wave}`)} ·{" "}
                      {h.report.won ? words("Sieg", "Victory") : words("Niederlage", "Defeat")}
                    </Button>
                  ))}
                </div>
                <RepairPanel battleId={last.battleId} />
              </details>
            </section>
          ) : null}
        </>
      )}
      {confirm && state ? (
        <Dialog
          title={
            confirm === "start"
              ? words("Portal betreten", "Enter portal")
              : words("Einsatz beenden", "Extract")
          }
          onClose={() => {
            if (!busy) setConfirm(null);
          }}
          actions={
            <>
              <Button disabled={busy} onClick={() => setConfirm(null)}>
                {words("Abbrechen", "Cancel")}
              </Button>
              <Button
                variant="gold"
                disabled={busy}
                data-testid="nexus-confirm"
                onClick={() => void act(confirm)}
              >
                {confirm === "start"
                  ? words(`${state.entryStamina} Treibstoff · Start`, `${state.entryStamina} fuel · Start`)
                  : words("Jetzt beenden", "Extract now")}
              </Button>
            </>
          }
        >
          <p>
            {confirm === "start"
              ? words(
                  "Ein Eintritt für fünf Wellen. Belohnungen jeder gewonnenen Welle gehen direkt ins Lager. Eine Niederlage beendet den Einsatz; verdiente Beute bleibt.",
                  "One entry for five waves. Each won wave pays directly into inventory. Defeat ends the run; earned loot stays yours.",
                )
              : words(
                  "Deine Beute bleibt im Lager. Dieser Eintritt wird verbraucht und der Einsatz kann nicht fortgesetzt werden.",
                  "Your loot stays in inventory. This entry is spent and this run cannot be resumed.",
                )}
          </p>
        </Dialog>
      ) : null}
    </section>
  );
}
